import { reactive, onUnmounted } from 'vue';
import { useTaskActions } from 'dashboard/modules/tasks/composables/useTaskActions';

const HEADER_HEIGHT = 40; // h-10 = 40px
const SNAP_MINUTES = 15;
const MIN_DRAG_DISTANCE = 4; // px — evita drag acidental em cliques simples

export function useTaskWeekDrag({
  visibleDays,
  columnRefs,
  allDayRefs,
  priorityBarStyle,
}) {
  const { updateTask } = useTaskActions();

  const dragState = reactive({
    draggingId: null,
    targetDayKey: null,
    targetIsAllDay: false,
  });

  let activeDrag = null;
  let clone = null;

  function dateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function snapMinutes(raw) {
    const snapped = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES;
    return Math.max(0, Math.min(snapped, 23 * 60 + 45));
  }

  function getTargetDayIndex(clientX) {
    const refs = columnRefs.value;
    for (let i = 0; i < visibleDays.value.length; i += 1) {
      const el = refs[i];
      if (el) {
        const rect = el.getBoundingClientRect();
        if (clientX >= rect.left && clientX <= rect.right) {
          return i;
        }
      }
    }
    return -1;
  }

  function isOverAllDayRow(clientY, dayIndex) {
    const el = allDayRefs.value[dayIndex];
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return clientY >= rect.top && clientY <= rect.bottom;
  }

  function getGridTop(dayIndex) {
    const el = columnRefs.value[dayIndex];
    if (!el) return null;
    return el.getBoundingClientRect().top + HEADER_HEIGHT;
  }

  function createClone(task, event, cardEl) {
    const rect = cardEl.getBoundingClientRect();
    const { className, style } = priorityBarStyle(task);

    const el = document.createElement('div');
    el.className = [
      'fixed',
      'z-[9999]',
      'pointer-events-none',
      'rounded',
      'px-1',
      'py-0.5',
      'text-xs',
      'shadow-lg',
      'opacity-90',
      'text-white',
      'truncate',
      className,
    ]
      .filter(Boolean)
      .join(' ');
    Object.assign(el.style, style);
    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
    el.style.left = `${event.clientX - activeDrag.offsetX}px`;
    el.style.top = `${event.clientY - activeDrag.offsetY}px`;
    el.textContent = task.title;

    document.body.appendChild(el);

    return el;
  }

  function onMousemove(event) {
    if (!activeDrag) return;

    const { startX, startY } = activeDrag;
    const moved =
      Math.abs(event.clientX - startX) > MIN_DRAG_DISTANCE ||
      Math.abs(event.clientY - startY) > MIN_DRAG_DISTANCE;

    if (!moved && !activeDrag.started) return;

    if (!activeDrag.started) {
      activeDrag.started = true;
      dragState.draggingId = activeDrag.task.id;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      clone = createClone(activeDrag.task, event, activeDrag.cardEl);
    }

    clone.style.left = `${event.clientX - activeDrag.offsetX}px`;
    clone.style.top = `${event.clientY - activeDrag.offsetY}px`;

    const dayIndex = getTargetDayIndex(event.clientX);
    if (dayIndex === -1) {
      activeDrag.targetDay = null;
      dragState.targetDayKey = null;
      return;
    }

    const targetDay = visibleDays.value[dayIndex];
    activeDrag.targetDay = targetDay;
    dragState.targetDayKey = dateKey(targetDay);

    if (isOverAllDayRow(event.clientY, dayIndex)) {
      activeDrag.targetIsAllDay = true;
      dragState.targetIsAllDay = true;
      activeDrag.snappedMinutes = null;
      return;
    }

    activeDrag.targetIsAllDay = false;
    dragState.targetIsAllDay = false;

    const gridTop = getGridTop(dayIndex);
    if (gridTop === null) return;
    const rawMinutes =
      event.clientY - gridTop - activeDrag.offsetY + HEADER_HEIGHT;
    activeDrag.snappedMinutes = snapMinutes(rawMinutes);
  }

  function onMouseup() {
    if (!activeDrag) return;

    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    if (clone) {
      clone.remove();
      clone = null;
    }

    dragState.draggingId = null;
    dragState.targetDayKey = null;
    dragState.targetIsAllDay = false;

    if (!activeDrag.started) {
      activeDrag = null;
      return;
    }

    const { task, targetDay, targetIsAllDay, snappedMinutes } = activeDrag;
    activeDrag = null;

    if (!targetDay) return;

    const original = new Date(task.due_date * 1000);
    const newDate = new Date(original);
    // targetDay e snappedMinutes vêm de posições na grade calculadas em
    // horário local — setFullYear/setHours (não as variantes UTC) evitam
    // deslocar a data/hora ao converter de volta para UTC no toISOString().
    newDate.setFullYear(
      targetDay.getFullYear(),
      targetDay.getMonth(),
      targetDay.getDate()
    );

    if (targetIsAllDay) {
      newDate.setHours(0, 0, 0, 0);
    } else {
      newDate.setHours(
        Math.floor(snappedMinutes / 60),
        snappedMinutes % 60,
        0,
        0
      );
    }

    if (newDate.getTime() === original.getTime()) return;

    updateTask({
      taskId: task.id,
      due_date: newDate.toISOString(),
    });
  }

  function onTaskMousedown(event, task, cardEl) {
    if (activeDrag) {
      document.removeEventListener('mousemove', onMousemove);
      document.removeEventListener('mouseup', onMouseup);
      if (clone) {
        clone.remove();
        clone = null;
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      dragState.draggingId = null;
      dragState.targetDayKey = null;
      dragState.targetIsAllDay = false;
      activeDrag = null;
    }

    event.preventDefault();

    const rect = cardEl.getBoundingClientRect();
    activeDrag = {
      task,
      cardEl,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      started: false,
      targetDay: null,
      targetIsAllDay: false,
      snappedMinutes: null,
    };

    document.addEventListener('mousemove', onMousemove);
    document.addEventListener('mouseup', onMouseup);
  }

  onUnmounted(() => {
    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    if (clone) {
      clone.remove();
      clone = null;
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    activeDrag = null;
  });

  return { onTaskMousedown, dragState };
}
